import React, { useEffect, useState } from 'react';
import { View, useWindowDimensions } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useT } from '@transifex/react';

import { useBiometricEnrollmentPrompt } from '../../biometric/BiometricEnrollmentProvider';
import { enrollBiometric } from '../../biometric/biometricEnrollment';
import { isBiometricEnrolled, wasBiometricDeclined } from '../../biometric/biometricStorage';
import { featureLabelFor } from '../../biometric/featureLabel';
import {
  useBiometricCapabilities,
  type BiometricType,
} from '../../biometric/useBiometricCapabilities';
import { BIOMETRIC_FLAG } from '../../config/runtimeConfig';
import { ROUTES, ERROR_MESSAGES } from '../../utils/constants';
import { screenTestId } from '../../utils/testId';
import { validateEmail, validatePassword } from '../../services/validationService';
import { completeAuth, handleLogin } from '../../services/authService';
import { analytics } from '../../utils/analytics';
import { useOnAuthenticated } from '../../hooks/useOnAuthenticated';

import { AuthFooter } from '../AuthFooter';
import { BloomButton } from '../BloomButton';
import { Checkbox } from '../Checkbox';
import { InlineError } from '../InlineError';
import { Input } from '../Input';
import { Logo } from '../Logo';
import { PasswordInput } from '../PasswordInput';
import ThemedText from '../ThemedText';
import { useLoginRouter } from '../../hooks/useLoginRouter';

interface LoginFormProps {
  onSSOClick: () => void;
  onTwoFactorRequired: (email: string, password: string, lastFourDigits: string) => void;
  /** True when the device has an enrolled biometric grant. Renders a retry button above the form. */
  biometricEligible?: boolean;
  /** Determines the label on the retry button ("Sign in with Face ID" / "fingerprint" / …). */
  biometricType?: BiometricType;
  /** Handler invoked when the biometric retry button is tapped. Kicks off the biometric prompt again. */
  onBiometricRetry?: () => void | Promise<unknown>;
  /** Seed value for the email field — e.g. the last biometric-enrolled email. */
  defaultEmail?: string | null;
}

const tid = screenTestId('Login');

export const LoginForm: React.FC<LoginFormProps> = ({
  onSSOClick,
  onTwoFactorRequired,
  biometricEligible = false,
  biometricType = null,
  onBiometricRetry,
  defaultEmail,
}) => {
  const router = useLoginRouter();
  const { width } = useWindowDimensions();
  const params = useLocalSearchParams();
  const t = useT();
  const onAuthenticated = useOnAuthenticated();
  const { promptIfEligible } = useBiometricEnrollmentPrompt();
  const biometricCapabilities = useBiometricCapabilities();

  const [email, setEmail] = useState(defaultEmail ?? '');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [bioOptInVisible, setBioOptInVisible] = useState(false);
  const [bioOptInChecked, setBioOptInChecked] = useState(false);

  useEffect(() => {
    if (biometricCapabilities.loading) return;
    // Reset checked whenever visibility drops so a stale tick can't
    // survive into a submit where the checkbox is no longer rendered.
    const hide = () => {
      setBioOptInVisible(false);
      setBioOptInChecked(false);
    };
    if (!biometricCapabilities.usable) {
      hide();
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [declined, enrolled, flagOn] = await Promise.all([
          wasBiometricDeclined(),
          isBiometricEnrolled(),
          analytics.isFeatureFlagEnabled(BIOMETRIC_FLAG),
        ]);
        if (cancelled) return;
        const shouldShow = flagOn && declined && !enrolled;
        setBioOptInVisible(shouldShow);
        if (!shouldShow) setBioOptInChecked(false);
      } catch {
        if (!cancelled) hide();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    biometricCapabilities.loading,
    biometricCapabilities.usable,
  ]);

  const nextUrl = typeof params.next === 'string' ? params.next : undefined;
  const returnTo = typeof params.return_to === 'string' ? params.return_to : undefined;
  const signInError = params.sign_in_error === 'true';
  const isDesktop = width >= 768;
  const bioOptInLabel = t('Enable {feature}', {
    feature: featureLabelFor(t, biometricCapabilities.type),
  });

  const handleMagicLinkClick = () => {
    analytics.trackEvent('magic_link_entry_clicked');
    // Intentionally does not propagate ?next= — the magic-link email URL
    // doesn't carry it either, so the round-trip is dead. Simpler to omit.
    router.push(ROUTES.MAGIC_LINK);
  };

  const handleSubmit = async () => {
    setEmailError(null);
    setPasswordError(null);
    setError(null);

    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);

    if (emailValidation || passwordValidation) {
      setEmailError(emailValidation);
      setPasswordError(passwordValidation);
      return;
    }

    setLoading(true);
    analytics.trackEvent('login_attempted');

    try {
      const result = await handleLogin({ email, password }, nextUrl);

      if (result.requiresTwoFactor) {
        analytics.trackEvent('2fa_shown');
        onTwoFactorRequired(email, password, result.lastFourDigits);
      } else {
        analytics.trackEvent('login_succeeded');
        if (bioOptInVisible && bioOptInChecked) {
          // Checkbox path bypasses promptIfEligible's declined-latch gate:
          // the checkbox IS the user's opt-in signal, so enrol directly.
          const enrollResult = await enrollBiometric({
            accessToken: result.token,
            email,
            promptMessage: bioOptInLabel,
            cancelLabel: t('Cancel'),
          });
          analytics.trackEvent('biometric_optin_from_login', {
            outcome: enrollResult.ok ? 'succeeded' : 'failed',
            ...(enrollResult.ok ? {} : { reason: enrollResult.reason }),
          });
        } else {
          // No-ops when ineligible/enrolled/declined. Fresh access required.
          await promptIfEligible({ accessToken: result.token, email });
        }
        await completeAuth({
          token: result.token,
          method: 'password',
          urlNextUrl: nextUrl,
          urlReturnTo: returnTo,
          nextApp: result.nextApp,
          onAuthenticated,
        });
      }
    } catch (err: any) {
      analytics.trackEvent('login_failed', { error_type: 'invalid_credentials' });
      setError(ERROR_MESSAGES.INVALID_CREDENTIALS);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View className='w-full'>
      <Logo />

      <View className='w-full'>
        <ThemedText type='titleHero' className='mb-2 font-bold' style={{ fontSize: 40, lineHeight: 48 }}>
          {t('Welcome back')}
        </ThemedText>
        <ThemedText
          type='bodyRegular'
          className='mb-8 text-login-gray-70'
          style={{ fontSize: isDesktop ? 32 : 20, lineHeight: isDesktop ? 45 : 28 }}
        >
          {t("Let's get you logged in to Vida Health")}
        </ThemedText>

        {signInError && (
          <InlineError message={t('The link you have used is either expired or invalid. Please log in using your email and password')} testID={tid('sign-in-error')} />
        )}
        <InlineError message={error} testID={tid('error')} />

        {biometricEligible && onBiometricRetry && (
          <View className='mb-4'>
            <BloomButton
              text={t('Sign in with {feature}', { feature: featureLabelFor(t, biometricType) })}
              onPress={() => { void onBiometricRetry(); }}
              hierarchy='secondary'
              testID={tid('biometric-retry-button')}
            />
          </View>
        )}

        <Input
          label={t('Email')}
          value={email}
          onChangeText={setEmail}
          placeholder={t('Email or username')}
          keyboardType='email-address'
          autoComplete='email'
          autoCorrect={false}
          spellCheck={false}
          maxLength={254}
          error={emailError || undefined}
          testID={tid('email-input')}
          id='id_username'
        />

        <PasswordInput
          label={t('Password')}
          value={password}
          onChangeText={setPassword}
          placeholder={t('Password')}
          autoComplete='current-password'
          error={passwordError || undefined}
          testID={tid('password-input')}
          onSubmitEditing={handleSubmit}
          id='id_password'
        />

        {bioOptInVisible && (
          <Checkbox
            label={bioOptInLabel}
            checked={bioOptInChecked}
            onChange={setBioOptInChecked}
            className='mb-4'
            testID={tid('bio-optin-checkbox')}
          />
        )}

        <View>
          <BloomButton
            text={t('Sign in')}
            onPress={handleSubmit}
            loading={loading}
            testID={tid('sign-in-button')}
          />
        </View>

        <AuthFooter
          variant='login'
          onSSOClick={onSSOClick}
          onMagicLinkClick={handleMagicLinkClick}
          testID={tid('footer')}
        />
      </View>
    </View>
  );
};
